import styled from '@emotion/styled';
import { useIsMobile } from '@stores/AppCommonData';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { textTruncate } from '@utils/textHelper';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import React, { forwardRef, useState } from 'react';
import Image from '@base/FutureImage';
import { localStorageAvailable } from '@utils/localStorage';
import segmentStorage from '@events/segmentStorage';
import { segment } from '@actions/analytics';
import { BUNDLE_BROCHURE_DOWNLOADED } from './constants';

const Wrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_14B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_0B};
    border-bottom: 1px solid var(--color-i-divider);
  }
`;

const Title = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const StyledButton = styled(Button)`
  width: 225px;
  height: ${Spacings.SPACING_12B};
  margin-top: ${Spacings.SPACING_6B};
  padding: ${Spacings.SPACING_0B};
  & .button--label-content {
    line-height: ${Spacings.SPACING_6B};
  }
  & .button--right-icon {
    height: ${Spacings.SPACING_6B};
    width: ${Spacings.SPACING_6B};
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: ${Spacings.SPACING_4B};
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const ReadMore = styled.span`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.P1};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  margin: ${Spacings.SPACING_0B};
  transition: all 300ms ease;
  margin-top: ${Spacings.SPACING_8B};
  color: var(--color-text-primary);
  cursor: pointer;
`;

const DescriptionWrapper = styled.div`
  margin-top: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_0B};
  }
`;

const Description = styled.span`
  white-space: pre-line;
  font-size: ${Fonts.P1};
  line-height: 20px;
  color: var(--color-text-primary);
`;

const TEXT_LIMIT = 456;
const MOBILE_TEXT_LIMIT = 180;

const About = forwardRef(({ data, goalSegmentData }, ref) => {
  const [isTextExpanded, setIsTextExpanded] = useState(false);

  const { description, brochure = true } = data;

  const isMobile = useIsMobile();

  const handleDownload = () => {
    if (localStorageAvailable()) {
      const notesSegmentData = JSON.parse(
        localStorage.getItem('notesSegmentData')
      );
      const lastPrimarySource = segmentStorage.getItem('lps');
      segment.track(BUNDLE_BROCHURE_DOWNLOADED, {
        ...goalSegmentData,
        ...notesSegmentData,
        last_primary_source: lastPrimarySource
      });
    }
    window.open(brochure);
  };

  const textLimit = isMobile ? MOBILE_TEXT_LIMIT : TEXT_LIMIT;

  return (
    <Wrapper ref={ref}>
      <Title variant={isMobile ? 'h5' : 'h4'}>About this combo</Title>
      <DescriptionWrapper
        onClick={() => {
          setIsTextExpanded((bool) => !bool);
        }}
      >
        {isTextExpanded ? (
          <Description>{description}</Description>
        ) : (
          <>
            <Description>{textTruncate(description, textLimit)}</Description>
            {description?.length > textLimit && <ReadMore>Read more</ReadMore>}
          </>
        )}
      </DescriptionWrapper>
      <StyledButton
        label="Download brochure"
        variant="secondary"
        size="large"
        startIcon={
          <Image
            src="store/notes/Toggles.svg"
            height={24}
            width={24}
            alt="download"
          />
        }
        onClick={handleDownload}
      />
    </Wrapper>
  );
});

About.displayName = 'About';
export default React.memo(About);
